import React from "react";

type State<Data, Err> = {
  status: "pending" | "success" | "error" | "idle";
  pending: boolean;
  data: Data | null;
  error: Err | null;
};

type Action<Data, Err> =
  | { type: "begin" }
  | { type: "success"; data: Data }
  | { type: "error"; error: Err };

function reducer<Data, Err>(
  state: State<Data, Err>,
  action: Action<Data, Err>
): State<Data, Err> {
  switch (action.type) {
    case "begin":
      return {
        ...state,
        status: "pending",
        pending: true,
        error: null
      };
    case "success":
      return {
        ...state,
        status: "success",
        pending: false,
        data: action.data,
        error: null
      };
    case "error":
      return {
        ...state,
        status: "error",
        pending: false,
        data: null,
        error: action.error
      };
  }
}

export function useAsyncFunction<Data, Err>(
  asyncFunction: () => Promise<Data>,
  immediate = true
): State<Data, Err> & {
  execute: () => Promise<void>;
} {
  type S = State<Data, Err>;
  type A = Action<Data, Err>;

  const [store, dispatch] = React.useReducer<(s: S, a: A) => S>(reducer, {
    status: "idle",
    pending: false,
    data: null,
    error: null
  } as State<Data, Err>);

  const execute = React.useCallback(() => {
    dispatch({ type: "begin" });
    return asyncFunction()
      .then(data => dispatch({ type: "success", data }))
      .catch((error: Err) => dispatch({ type: "error", error }));
  }, [asyncFunction]);

  React.useEffect(() => {
    if (immediate) {
      execute();
    }
  }, [execute, immediate]);

  return { execute, ...store };
}
